import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import CampoCupom from './CampoCupom';
import { useCheckoutViewModel } from './CheckoutFormViewModel';
import CheckoutSucesso from './CheckoutSucesso';
import { CTA } from './checkoutUi';
import ComprovanteStep from './ComprovanteStep';
import DadosStep from './DadosStep';
import EventoEspecialStep from './EventoEspecialStep';
import HorarioStep from './HorarioStep';
import PagamentoStep from './PagamentoStep';
import PlanStep, { PLANOS } from './PlanStep';
import ResumoPedido from './ResumoPedido';
import { useIsDesktop } from './useIsDesktop';

const CARD =
  'rounded-[20px] border border-[#eef0f4] bg-white p-4 lg:rounded-[28px] lg:px-9 lg:py-8 lg:shadow-[0_20px_50px_rgba(255,35,104,.06)]';

const emReais = (centavos) => (centavos / 100).toFixed(2).replace('.', ',');

const CheckoutFormView = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const vm = useCheckoutViewModel();
  const {
    eventoInfo,
    loading,
    horarioSelecionadoId,
    planoSelecionado,
    metodoPagamento,
    isSubmitting,
    etapa,
    setEtapa,
    termoAceito,
    termosCondicoesAceitos,
    isEventoEspecial,
    cupom,
    setCupom,
  } = vm;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fff8f9] flex items-center justify-center text-xs font-bold text-[#8a94a8] uppercase tracking-widest">
        Preparando seu checkout seguro...
      </div>
    );
  }

  if (!eventoInfo) {
    return (
      <div className="min-h-screen bg-[#fff8f9] flex items-center justify-center text-xs font-bold text-red-400 uppercase tracking-widest">
        Sessão ou evento não encontrado.
      </div>
    );
  }

  const assinatura = planoSelecionado === 'assinatura';
  const social = planoSelecionado === 'ingresso_social';
  const planoNome = PLANOS.find((p) => p.id === planoSelecionado)?.nome ?? null;

  /* ── Valores ───────────────────────────────────────────────────── */
  const valorBasePlano = !planoSelecionado
    ? null
    : assinatura
      ? (eventoInfo.valorMembroCentavos ?? 6499)
      : social
        ? (eventoInfo.valorSocialCentavos ?? 2999)
        : (eventoInfo.valorCentavos ?? 8399);
  const addOnsTotal = (eventoInfo.addOns || []).reduce(
    (sum, addon) => sum + addon.priceInCents,
    0,
  );
  const subtotalCentavos =
    valorBasePlano != null ? valorBasePlano + addOnsTotal : null;
  const descontoCentavos = cupom?.descontoCentavos ?? 0;
  const valorExibicao =
    subtotalCentavos != null
      ? emReais(subtotalCentavos - descontoCentavos)
      : null;

  /* ── Passos ────────────────────────────────────────────────────── */
  const passos = isEventoEspecial
    ? [
        { id: 'evento_especial', label: 'Confirmação' },
        { id: 'dados', label: 'Dados pessoais' },
        { id: 'escolhas', label: 'Escolha do horário' },
        { id: 'pagamento', label: 'Pagamento' },
      ]
    : [
        { id: 'plano', label: 'Escolha do plano' },
        { id: 'dados', label: 'Dados pessoais' },
        ...(social ? [{ id: 'comprovante', label: 'Comprovante' }] : []),
        {
          id: 'escolhas',
          label: assinatura ? 'Horário preferencial' : 'Escolha do horário',
        },
        { id: 'pagamento', label: 'Pagamento' },
      ];
  const concluido = etapa === 'sucesso';
  const indice = concluido
    ? passos.length
    : passos.findIndex((p) => p.id === etapa);

  const voltarEtapa = () => {
    if (etapa === 'dados')
      setEtapa(isEventoEspecial ? 'evento_especial' : 'plano');
    else if (etapa === 'comprovante') setEtapa('dados');
    else if (etapa === 'escolhas') setEtapa(social ? 'comprovante' : 'dados');
    else if (etapa === 'pagamento') setEtapa('escolhas');
    else if (etapa === 'sucesso')
      setEtapa(isEventoEspecial ? 'evento_especial' : 'plano');
    else navigate(-1);
  };

  /* ── Botão principal: no card (web) ou no rodapé fixo (celular).
        Comprovante e confirmação de evento têm o botão dentro da própria etapa. */
  const cta = {
    plano: {
      label: planoNome ? `Continuar com ${planoNome}` : 'Selecione um ingresso',
      onClick: () => setEtapa('dados'),
      disabled: !planoSelecionado,
    },
    dados: {
      label: 'Continuar para horário',
      onClick: vm.handleAvancarParaEscolhas,
    },
    escolhas: {
      label: 'Avançar para pagamento',
      onClick: () => setEtapa('pagamento'),
    },
    pagamento: {
      label: isSubmitting
        ? 'Processando…'
        : assinatura
          ? 'Ativar meu plano'
          : 'Garantir minha vaga',
      onClick: () => vm.handleFinalizarPedido(true),
      disabled:
        isSubmitting || !termosCondicoesAceitos || (assinatura && !termoAceito),
    },
  }[etapa];

  const botaoPrincipal = cta && (
    <button
      type="button"
      className={CTA}
      onClick={cta.onClick}
      disabled={cta.disabled}
    >
      {cta.label}
    </button>
  );

  /* ── Textos do resumo ──────────────────────────────────────────── */
  const horarios = eventoInfo.horariosDisponiveis || [];
  const horarioAtivo = horarios.find((h) => h.id === horarioSelecionadoId);
  const sessao = horarioAtivo
    ? `${horarioAtivo.numero} ${horarioAtivo.semana} · ${horarioAtivo.horario}`
    : null;
  const datas = horarios
    .map((h) => h.dataFormatada)
    .filter((v, i, arr) => v && arr.indexOf(v) === i)
    .join(' · ');
  const quando = [datas, eventoInfo.local].filter(Boolean).join(' — ');

  // O ingresso social nunca aceita cupom; depois de gerada a cobrança o
  // campo some.
  const campoCupom = Boolean(planoSelecionado) && !social && !concluido && (
    <CampoCupom
      cupom={cupom}
      validar={vm.validarCupom}
      onAplicar={setCupom}
      onRemover={() => setCupom(null)}
    />
  );

  return (
    <div className="min-h-screen bg-[#fff8f9] font-sans text-[#0f1729]">
      <div
        className={`mx-auto flex max-w-[1280px] flex-col lg:gap-5 lg:px-14 lg:pb-12 lg:pt-8 ${!concluido ? 'pb-40' : 'pb-8'}`}
      >
        {/* ── Topo web: voltar + passos ───────────────────────────── */}
        <div className="hidden items-center gap-6 border-b border-[#eef0f4] pb-4 text-[11px] font-bold uppercase tracking-[.06em] lg:flex">
          <button
            type="button"
            onClick={voltarEtapa}
            className="flex cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-[11px] font-bold uppercase tracking-[.06em] text-[#8a94a8] hover:text-[#ff2368]"
          >
            <ArrowLeft size={14} />
            {concluido ? 'Voltar ao início' : 'Voltar'}
          </button>
          <ol className="m-0 ml-auto flex list-none gap-5 p-0">
            {passos.map((p, i) => (
              <li
                key={p.id}
                aria-current={i === indice ? 'step' : undefined}
                className={
                  i === indice
                    ? 'text-[#ff2368]'
                    : i < indice
                      ? 'text-[#0f1729]'
                      : 'text-[#8a94a8]'
                }
              >
                {i + 1}. {p.label}
                {i < passos.length - 1 && (
                  <span aria-hidden="true" className="ml-5 text-[#8a94a8]">
                    ›
                  </span>
                )}
              </li>
            ))}
          </ol>
        </div>

        {/* ── Topo celular: voltar + etapa + contador + progresso ─── */}
        <div className="lg:hidden">
          <div className="flex items-center gap-2 px-4 pb-2.5 pt-3 text-[11px] font-bold uppercase tracking-[.06em]">
            <button
              type="button"
              onClick={voltarEtapa}
              aria-label={concluido ? 'Voltar ao início' : 'Voltar'}
              className="-ml-3 grid h-11 w-11 cursor-pointer place-items-center border-0 bg-transparent p-0 text-[#0f1729]"
            >
              <ArrowLeft size={20} />
            </button>
            <span>{concluido ? 'Pedido' : passos[indice]?.label}</span>
            {!concluido && (
              <span className="ml-auto text-[#8a94a8]">
                {indice + 1} de {passos.length}
              </span>
            )}
          </div>
          <div
            className="grid gap-1 px-4"
            style={{
              gridTemplateColumns: `repeat(${passos.length}, minmax(0, 1fr))`,
            }}
            aria-hidden="true"
          >
            {passos.map((p, i) => (
              <div
                key={p.id}
                className={`h-[3px] rounded-full ${i <= indice ? 'bg-[#ff2368]' : 'bg-[#eef0f4]'}`}
              />
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 items-start gap-3.5 p-4 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-7 lg:p-0">
          <div className="flex min-w-0 flex-col gap-3.5">
            {/* Evento — no celular fica sempre no topo do conteúdo */}
            <div className="flex flex-col gap-0.5 rounded-[20px] border border-[#eef0f4] bg-white px-4 py-3.5 lg:hidden">
              <span className="text-sm font-bold leading-[1.3]">
                {eventoInfo.titulo}
              </span>
              {quando && (
                <span className="text-[11px] text-[#4b5568]">{quando}</span>
              )}
            </div>

            <div className={`${CARD} flex flex-col gap-6`}>
              {concluido ? (
                <CheckoutSucesso
                  paymentResult={vm.paymentResult}
                  valorExibicao={valorExibicao}
                  formattedTime={vm.formattedTime}
                  copiarPixCopiaECola={vm.copiarPixCopiaECola}
                  pixCopiado={vm.pixCopiado}
                  onInicio={() => navigate('/')}
                  onCancelar={() => setEtapa('plano')}
                />
              ) : (
                <>
                  {etapa === 'evento_especial' && (
                    <EventoEspecialStep
                      eventoInfo={eventoInfo}
                      onContinuar={() => setEtapa('dados')}
                    />
                  )}
                  {etapa === 'plano' && (
                    <PlanStep
                      planoSelecionado={planoSelecionado}
                      setPlanoSelecionado={vm.setPlanoSelecionado}
                      eventoInfo={eventoInfo}
                    />
                  )}
                  {etapa === 'dados' && (
                    <DadosStep
                      dadosBase={vm.dadosBase}
                      handleInputChange={vm.handleInputChange}
                      erros={vm.erros}
                      statusUsuario={vm.statusUsuario}
                    />
                  )}
                  {etapa === 'comprovante' && (
                    <ComprovanteStep
                      onContinuar={({ url, nota, mimetype }) => {
                        vm.setSocialDocumentUrl(url);
                        vm.setSocialDocumentNote(nota);
                        window.socialDocumentMimeType = mimetype;
                        setEtapa('escolhas');
                      }}
                    />
                  )}
                  {etapa === 'escolhas' && (
                    <HorarioStep
                      horarios={horarios}
                      selecionadoId={horarioSelecionadoId}
                      onSelect={vm.setHorarioSelecionadoId}
                      assinatura={assinatura}
                      erro={vm.erros?.horario}
                    />
                  )}
                  {etapa === 'pagamento' && (
                    <PagamentoStep
                      assinatura={assinatura}
                      somentePix={eventoInfo.somentePix}
                      metodoPagamento={metodoPagamento}
                      setMetodoPagamento={vm.setMetodoPagamento}
                      dadosCartao={vm.dadosCartao}
                      handleInputChange={vm.handleInputChange}
                      erros={vm.erros}
                      termoAceito={termoAceito}
                      setTermoAceito={vm.setTermoAceito}
                      termosCondicoesAceitos={termosCondicoesAceitos}
                      setTermosCondicoesAceitos={vm.setTermosCondicoesAceitos}
                    />
                  )}
                  {/* Celular não tem resumo lateral: o cupom entra aqui,
                      na etapa em que o valor é pago. */}
                  {!isDesktop && etapa === 'pagamento' && campoCupom}
                  {isDesktop && botaoPrincipal}
                </>
              )}
            </div>
          </div>

          {isDesktop && (
            <ResumoPedido
              planoNome={planoNome}
              titulo={eventoInfo.titulo}
              quando={quando}
              sessao={sessao}
              campoCupom={campoCupom}
              cupom={cupom}
              subtotal={
                subtotalCentavos != null ? emReais(subtotalCentavos) : null
              }
              desconto={emReais(descontoCentavos)}
              total={valorExibicao}
            />
          )}
        </div>
      </div>

      {/* ── Rodapé fixo (celular): total + botão principal ─────────── */}
      {!isDesktop && !concluido && (
        <div className="fixed inset-x-0 bottom-0 z-20 flex flex-col gap-2.5 rounded-t-3xl border-t border-[#eef0f4] bg-white px-4 pt-3.5 shadow-[0_-10px_30px_rgba(255,35,104,.08)] pb-[calc(22px+env(safe-area-inset-bottom,0px))]">
          <div className="flex items-center justify-between gap-3">
            <span className="flex min-w-0 flex-col">
              <span className="text-[10px] font-bold uppercase text-[#8a94a8]">
                Total{planoNome ? ` · ${planoNome}` : ''}
              </span>
              <span className="text-[11px] text-[#4b5568]">
                {cupom
                  ? `Cupom ${cupom.codigo}: − R$ ${emReais(descontoCentavos)}`
                  : (sessao ?? ' ')}
              </span>
            </span>
            <span className="text-xl font-extrabold tabular-nums">
              {valorExibicao ? `R$ ${valorExibicao}` : '—'}
            </span>
          </div>
          {botaoPrincipal}
        </div>
      )}
    </div>
  );
};

export default CheckoutFormView;
